import { useState } from 'react';
import { Calendar, Copy, Check, Clock, Briefcase, Users, CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar as CalendarUI } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import GlowCard from '@/components/GlowCard';
import { cn } from '@/lib/utils';
import { useToolUsage } from '@/hooks/useToolUsage';
import { toast } from 'sonner';
import { format, differenceInDays, differenceInWeeks, differenceInMonths, differenceInYears, addDays, subDays, isWeekend, eachDayOfInterval, startOfYear, endOfYear } from 'date-fns';

const DaysCalculator = () => {
  useToolUsage('days_calculator'); // Track tool usage

  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [daysToAdd, setDaysToAdd] = useState(30);
  const [isSubtract, setIsSubtract] = useState(false);
  const [includeEndDate, setIncludeEndDate] = useState(true);
  const [result, setResult] = useState<string>('');
  const [copied, setCopied] = useState(false);

  const calculateDaysBetween = () => {
    if (!startDate || !endDate) {
      toast.error('Please select both dates');
      return;
    }

    const adjustment = includeEndDate ? 1 : 0;
    const days = Math.abs(differenceInDays(endDate, startDate)) + adjustment;
    const weeks = differenceInWeeks(endDate, startDate);
    const months = differenceInMonths(endDate, startDate);
    const years = differenceInYears(endDate, startDate);

    setResult(`${days} days (${Math.abs(weeks)} weeks, ${Math.abs(months)} months, ${Math.abs(years)} years)`);
    toast.success('Calculated!');
  };

  const calculateAddSubtract = () => {
    if (!startDate) {
      toast.error('Please select a start date');
      return;
    }

    const newDate = isSubtract ? subDays(startDate, daysToAdd) : addDays(startDate, daysToAdd);
    setResult(`New date: ${format(newDate, 'MMMM d, yyyy')} (${format(newDate, 'EEEE')})`);
    toast.success('Calculated!');
  };

  const calculateAge = () => {
    if (!startDate) {
      toast.error('Please select your birth date');
      return;
    }

    const today = new Date();
    let years = today.getFullYear() - startDate.getFullYear();
    const m = today.getMonth() - startDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < startDate.getDate())) {
      years--;
    }

    const months = Math.abs((today.getMonth() - startDate.getMonth() + 12) % 12);
    const days = Math.abs(today.getDate() - startDate.getDate());
    const totalDays = differenceInDays(today, startDate);

    // Next birthday
    const nextBirthday = new Date(today.getFullYear(), startDate.getMonth(), startDate.getDate());
    if (nextBirthday < today) {
      nextBirthday.setFullYear(nextBirthday.getFullYear() + 1);
    }
    const daysUntilBirthday = differenceInDays(nextBirthday, today);

    setResult(`${years} years, ${months} months, ${days} days old\n(${totalDays.toLocaleString()} total days)\nNext birthday in ${daysUntilBirthday} days`);
    toast.success('Age calculated!');
  };

  const calculateWorkdays = () => {
    if (!startDate || !endDate) {
      toast.error('Please select both dates');
      return;
    }

    const allDays = eachDayOfInterval({ start: startDate, end: endDate });
    const workdays = allDays.filter(day => !isWeekend(day)).length;
    const weekends = allDays.length - workdays;

    setResult(`${workdays} working days\n(${weekends} weekend days excluded)`);
    toast.success('Workdays calculated!');
  };

  const getQuickStats = () => {
    const today = new Date();
    const newYear = new Date(today.getFullYear() + 1, 0, 1);
    const daysUntilNewYear = differenceInDays(newYear, today);

    const dayOfWeek = today.getDay();
    const daysUntilFriday = dayOfWeek <= 5 ? 5 - dayOfWeek : 7 - dayOfWeek + 5;

    const yearEnd = endOfYear(today);
    const yearStart = startOfYear(today);
    const daysInYear = differenceInDays(yearEnd, yearStart) + 1;
    const dayOfYear = differenceInDays(today, yearStart) + 1;
    const yearProgress = Math.round((dayOfYear / daysInYear) * 100);

    return { daysUntilNewYear, daysUntilFriday, dayOfYear, yearProgress };
  };

  const quickStats = getQuickStats();

  const handleCopy = async () => {
    if (!result) return;
    await navigator.clipboard.writeText(result);
    setCopied(true);
    toast.success('Copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const DatePicker = ({ date, setDate, label }: { date: Date | undefined; setDate: (date: Date | undefined) => void; label: string }) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-full justify-start text-left font-normal",
              !date && "text-muted-foreground"
            )}
          >
            <CalendarDays className="mr-2 h-4 w-4" />
            {date ? format(date, "PPP") : "Pick a date"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-popover" align="start">
          <CalendarUI
            mode="single"
            selected={date}
            onSelect={setDate}
            initialFocus
            className="pointer-events-auto"
          />
        </PopoverContent>
      </Popover>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/10">
          <Calendar className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Days Calculator</h1>
          <p className="text-muted-foreground">Calculate days between dates, age, and more</p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <GlowCard className="p-4 text-center card-lift">
          <p className="text-2xl font-bold text-primary">{quickStats.daysUntilNewYear}</p>
          <p className="text-xs text-muted-foreground">Days until New Year</p>
        </GlowCard>
        <GlowCard className="p-4 text-center card-lift">
          <p className="text-2xl font-bold text-success">{quickStats.daysUntilFriday}</p>
          <p className="text-xs text-muted-foreground">Days until Friday</p>
        </GlowCard>
        <GlowCard className="p-4 text-center card-lift">
          <p className="text-2xl font-bold text-warning">{quickStats.dayOfYear}</p>
          <p className="text-xs text-muted-foreground">Day of Year</p>
        </GlowCard>
        <GlowCard className="p-4 text-center card-lift">
          <p className="text-2xl font-bold text-blue-500">{quickStats.yearProgress}%</p>
          <p className="text-xs text-muted-foreground">Year Progress</p>
        </GlowCard>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Calculator */}
        <GlowCard className="card-lift">
          <Tabs defaultValue="between" className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-6">
              <TabsTrigger value="between" className="text-xs">
                <Clock className="h-3 w-3 mr-1" />
                Days Between
              </TabsTrigger>
              <TabsTrigger value="addsubtract" className="text-xs">
                <CalendarDays className="h-3 w-3 mr-1" />
                Add/Subtract
              </TabsTrigger>
              <TabsTrigger value="age" className="text-xs">
                <Users className="h-3 w-3 mr-1" />
                Age
              </TabsTrigger>
              <TabsTrigger value="workdays" className="text-xs">
                <Briefcase className="h-3 w-3 mr-1" />
                Workdays
              </TabsTrigger>
            </TabsList>

            <TabsContent value="between" className="space-y-4">
              <DatePicker date={startDate} setDate={setStartDate} label="Start Date" />
              <DatePicker date={endDate} setDate={setEndDate} label="End Date" />

              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <Label htmlFor="include-end">Include end date in count</Label>
                <Switch id="include-end" checked={includeEndDate} onCheckedChange={setIncludeEndDate} />
              </div>

              <Button onClick={calculateDaysBetween} className="w-full hover-bounce ripple">
                <Clock className="h-4 w-4 mr-2" />
                Calculate Days
              </Button>
            </TabsContent>

            <TabsContent value="addsubtract" className="space-y-4">
              <DatePicker date={startDate} setDate={setStartDate} label="Start Date" />

              <div className="space-y-2">
                <Label>Days to {isSubtract ? 'subtract' : 'add'}</Label>
                <Input
                  type="number"
                  value={daysToAdd}
                  onChange={(e) => setDaysToAdd(parseInt(e.target.value) || 0)}
                  min={0}
                />
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <Label htmlFor="subtract-mode">Subtract instead of add</Label>
                <Switch id="subtract-mode" checked={isSubtract} onCheckedChange={setIsSubtract} />
              </div>

              <Button onClick={calculateAddSubtract} className="w-full hover-bounce ripple">
                <CalendarDays className="h-4 w-4 mr-2" />
                Calculate Date
              </Button>
            </TabsContent>

            <TabsContent value="age" className="space-y-4">
              <DatePicker date={startDate} setDate={setStartDate} label="Birth Date" />

              <Button onClick={calculateAge} className="w-full hover-bounce ripple">
                <Users className="h-4 w-4 mr-2" />
                Calculate Age
              </Button>
            </TabsContent>

            <TabsContent value="workdays" className="space-y-4">
              <DatePicker date={startDate} setDate={setStartDate} label="Start Date" />
              <DatePicker date={endDate} setDate={setEndDate} label="End Date" />

              <Button onClick={calculateWorkdays} className="w-full hover-bounce ripple">
                <Briefcase className="h-4 w-4 mr-2" />
                Calculate Workdays
              </Button>
            </TabsContent>
          </Tabs>
        </GlowCard>

        {/* Result */}
        <GlowCard className="card-lift">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              Result
            </h2>
            {result && (
              <Button variant="outline" size="sm" onClick={handleCopy} className="hover-bounce">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            )}
          </div>

          {result ? (
            <div className="p-6 rounded-lg bg-muted/50 text-center animate-fade-in">
              <pre className="text-lg font-semibold whitespace-pre-wrap">{result}</pre>
            </div>
          ) : (
            <div className="p-12 rounded-lg bg-muted/30 text-center">
              <Calendar className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
              <p className="text-muted-foreground">Select dates and calculate</p>
            </div>
          )}

          <div className="mt-4 p-3 rounded-lg bg-primary/5 border border-primary/20">
            <p className="text-xs text-muted-foreground">
              💡 <strong>Tip:</strong> Use the quick stats above to see days until common events.
            </p>
          </div>
        </GlowCard>
      </div>
    </div>
  );
};

export default DaysCalculator;
